import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import useFetch from "src/Components/Common/useFetch";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";

const EntryDetailsDisputedEntries = ({
  selectedRowOfDisputedEntryForModal,
  openEntryDetailsModal,
  setOpenEntryDetailsModal,
}) => {
  const [entryData, setEntryData] = useState({
    amountDifference: null,
    amountDifferencePercent: null,
    vendorData: [],
    instamartData: [],
  });
  const handleClose = () => {
    setOpenEntryDetailsModal(false);
  };

  // Common styles
  const commonStyles = {
    sectionTitle: {
      fontSize: "12px",
      fontWeight: 600,
      color: "rgba(0,0,0,0.87)",
      letterSpacing: "0.012px",
      fontFamily: "Inter",
    },
    tableContainer: {
      border: "1px solid rgba(0,0,0,0.12)",
      borderRadius: "8px",
      overflow: "hidden",
      backgroundColor: "#fafafa",
    },
    headerCell: {
      padding: "11px 16px",
      backgroundColor: "#fafafa",
      borderBottom: "1px solid rgba(0,0,0,0.12)",
      borderRight: "1px solid rgba(0,0,0,0.12)",
      fontSize: "12px",
      fontWeight: 600,
      color: "rgba(0,0,0,0.87)",
      letterSpacing: "0.012px",
      fontFamily: "Inter",
      height: "38px",
      display: "flex",
      alignItems: "center",
    },
    lastHeaderCell: {
      padding: "11px 16px",
      backgroundColor: "#fafafa",
      borderBottom: "1px solid rgba(0,0,0,0.12)",
      fontSize: "12px",
      fontWeight: 600,
      color: "rgba(0,0,0,0.87)",
      letterSpacing: "0.012px",
      fontFamily: "Inter",
      height: "38px",
      display: "flex",
      alignItems: "center",
    },
    dataCell: {
      padding: "11px 16px",
      backgroundColor: "#ffffff",
      borderRight: "1px solid rgba(0,0,0,0.12)",
      fontSize: "12px",
      fontWeight: 400,
      color: "rgba(0,0,0,0.87)",
      letterSpacing: "0.1176px",
      fontFamily: "Inter",
      height: "32px",
      display: "flex",
      alignItems: "center",
    },
    lastDataCell: {
      padding: "11px 16px",
      backgroundColor: "#ffffff",
      fontSize: "12px",
      fontWeight: 400,
      color: "rgba(0,0,0,0.87)",
      letterSpacing: "0.1176px",
      fontFamily: "Inter",
      height: "32px",
      display: "flex",
      alignItems: "center",
    },
  };

  // Column configuration
  const columns = [
    { key: "referenceNo", label: "Reference No." },
    { key: "documentDate", label: "Document Date" },
    { key: "amount", label: "Amount" },
    { key: "entryNumber", label: "Entry Number" },
    { key: "postingDate", label: "Posting date" },
    { key: "paymentId", label: "Payment ID" },
    { key: "otherUniqueId", label: "Other Unique Id" },
    { key: "documentType", label: "Document Type" },
    { key: "entity", label: "Entity" },
  ];

  // Common Column Component
  const DataColumn = ({ column, data, isLast }) => (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Box sx={isLast ? commonStyles.lastHeaderCell : commonStyles.headerCell}>{column.label}</Box>
      {data.map((item, index) => (
        <Box
          key={index}
          sx={{
            ...(isLast ? commonStyles.lastDataCell : commonStyles.dataCell),
            borderBottom: index < data.length - 1 ? "1px solid rgba(0,0,0,0.12)" : "none",
          }}
        >
          {item[column.key]}
        </Box>
      ))}
    </Box>
  );

  // Common Data Table Component
  const DataTable = ({ title, data }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={commonStyles.sectionTitle}>{title}</Box>
      <Box sx={commonStyles.tableContainer}>
        <Box sx={{ display: "flex", width: "100%" }}>
          {columns.map((column, index) => (
            <DataColumn key={column.key} column={column} data={data} isLast={index === columns.length - 1} />
          ))}
        </Box>
      </Box>
    </Box>
  );

  // api--------------------------

  useEffect(() => {
    getTakeActionDataActionEntryDetails(selectedRowOfDisputedEntryForModal?.gapId);
  }, []);

  const getTakeActionDataActionEntryDetails = async (gapId) => {
    await useFetch(API_ENDPOINTS.FETCH_DR_ENTRIES_ENTRY_DETAILS(gapId).url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_DR_ENTRIES_ENTRY_DETAILS(gapId).failureMessage,
      thenCallBack: (response) => {
        setEntryData(response.data.data);
      },
      catchCallBack: () => {},
    });
  };

  // ---------------

  return (
    <>
      <Dialog open={openEntryDetailsModal} maxWidth={DIALOG_MAX_WIDTHS.LG} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">{selectedRowOfDisputedEntryForModal?.businessPartnerName || ""}</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Amount Difference Summary Table */}
            <Box
              sx={{
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#ffffff",
                marginBottom: 2,
              }}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "rgba(0,0,0,0.87)",
                        borderBottom: "1px solid rgba(0,0,0,0.12)",
                        borderRight: "1px solid rgba(0,0,0,0.12)",
                        padding: "11px 16px",
                        backgroundColor: "#f5f5f5",
                        width: "50%",
                      }}
                      className="body-s-bold"
                    >
                      Amount difference
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "rgba(0,0,0,0.87)",
                        borderBottom: "1px solid rgba(0,0,0,0.12)",
                        padding: "11px 16px",
                        backgroundColor: "#f5f5f5",
                        width: "50%",
                      }}
                      className="body-s-bold"
                    >
                      Amount difference (%)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        color: "rgba(0,0,0,0.87)",
                        borderRight: "1px solid rgba(0,0,0,0.12)",
                        padding: "11px 16px",
                        backgroundColor: "transparent",
                      }}
                      className="body-s-regular"
                    >
                      {formatMoney(ToDineroObj(entryData.amountDifference))}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        color: "rgba(0,0,0,0.87)",
                        padding: "11px 16px",
                        backgroundColor: "transparent",
                      }}
                      className="body-s-regular"
                    >
                      {entryData.amountDifferencePercent}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {/* Entry Details Tables */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <DataTable title="Vendor Side" data={entryData.vendorData} />
              <DataTable title="Instamart Side" data={entryData.instamartData} />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EntryDetailsDisputedEntries;
